import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useTenant, useIsAgencyTenant } from './context/TenantContext';
import CatalogSearch from './components/CatalogSearch';
import ReceiptAnalyzer from './components/ReceiptAnalyzer';
import { SV_DEPARTAMENTOS } from './utils/svDepartamentos';
import AgencyStep2 from './components/AgencyEncomienda/AgencyStep2';
import useAgencyStep2Controller from './components/AgencyEncomienda/useAgencyStep2Controller';
import { mapItemAgency, recipientDesdePaqueteRow, mismoRecipient } from './components/AgencyEncomienda/agencyMapper';
import * as agencyApi from './api/supabase/agencyEncomienda';
import { insertBox, BOX_NUMBER_ERROR } from './utils/insertBox';
import {
  ChevronDown, ChevronUp, CheckCircle, AlertCircle,
  Plus, X, Loader, Languages, ExternalLink
} from 'lucide-react';

// ── Translations ──────────────────────────────────────────────────────────────
const wt = {
  es: {
    step1Label: 'Remitente y Destinatario',
    step2Label: 'Cajas y Artículos',
    step3Label: 'Revisión y Confirmación',
    senderSection: 'Información del Remitente',
    senderPhone: 'Teléfono del remitente',
    senderName: 'Nombre completo del remitente',
    registeredClient: 'Cliente registrado ✓',
    newClient: 'Cliente nuevo — se registrará al guardar',
    recipientSection: 'DESTINATARIO',
    recipientName: 'Nombre del destinatario *',
    recipientPhone: 'Teléfono del destinatario',
    recipientAddress: 'Dirección completa *',
    pickUp: '📦 Pick Up',
    dropOff: '🚚 Drop Off',
    saveAndContinue: 'Guardar y continuar →',
    clientMayLeave: '✓ Información guardada. El cliente puede retirarse.',
    hasDraft: 'Tiene un borrador guardado. ¿Desea continuarlo?',
    continueDraft: 'Continuar borrador',
    newEntry: 'Nuevo registro',
    boxesSection: 'CAJAS',
    addBox: '+ Agregar Caja',
    boxWeight: 'Peso (lbs)',
    boxDimensions: 'Dimensiones (ej: 12x5x17)',
    cajaLabel: 'CAJA',
    addItem: '+ Agregar artículo',
    itemQty: 'Cant',
    itemDesc: 'Descripción del artículo',
    itemCondition: 'Condición',
    itemUnitValue: 'Valor Unit. ($)',
    itemTotal: 'Total',
    backButton: '← Volver',
    reviewTotals: 'Revisar totales →',
    confirmButton: '✓ Confirmar y Guardar Encomienda',
    backToEdit: '← Volver a editar',
    successMsg: '✓ Encomienda {id} guardada exitosamente',
    totalItems: 'Total artículos',
    totalFOB: 'Valor FOB',
    estimatedTax: 'Impuesto est. (14%)',
    encomiendaId: 'Encomienda ID',
    sender: 'Remitente',
    totalPackages: 'Paquetes',
    totalFOBLabel: 'FOB Total',
    totalTax: 'Impuesto Total',
    nuevo: 'NUEVO',
    usado: 'USADO',
    grandTotals: 'Totales Generales',
    loadingReview: 'Cargando revisión...',
    noData: 'No se encontraron datos.',
    itemsCount: 'artículos',
    saveForLater: 'Guardar y continuar después',
    savedDraftMsg: '✓ Borrador guardado. Retome la encomienda desde el panel superior.',
    updateButton: '✓ Actualizar Encomienda',
    packageItems: 'ARTÍCULOS',
    cajasTotal: 'Total',
    cajas: 'cajas',
    lbs: 'lbs',
    cajasRegistradas: 'CAJAS REGISTRADAS',
    paqueteLabel: 'Paquete',
    addPackage: '+ Agregar Paquete',
  },
  en: {
    step1Label: 'Sender & Recipient',
    step2Label: 'Boxes & Items',
    step3Label: 'Review & Confirm',
    senderSection: 'Sender Information',
    senderPhone: "Sender's phone number",
    senderName: 'Full name of sender',
    registeredClient: 'Registered client ✓',
    newClient: 'New client — will be registered on save',
    recipientSection: 'RECIPIENT',
    recipientName: 'Recipient name *',
    recipientPhone: 'Recipient phone',
    recipientAddress: 'Full address *',
    pickUp: '📦 Pick Up',
    dropOff: '🚚 Drop Off',
    saveAndContinue: 'Save and continue →',
    clientMayLeave: '✓ Information saved. The client may leave.',
    hasDraft: 'You have a saved draft. Would you like to continue?',
    continueDraft: 'Continue draft',
    newEntry: 'New entry',
    boxesSection: 'BOXES',
    addBox: '+ Add Box',
    boxWeight: 'Weight (lbs)',
    boxDimensions: 'Dimensions (e.g. 12x5x17)',
    cajaLabel: 'BOX',
    addItem: '+ Add item',
    itemQty: 'Qty',
    itemDesc: 'Item description',
    itemCondition: 'Condition',
    itemUnitValue: 'Unit Value ($)',
    itemTotal: 'Total',
    backButton: '← Back',
    reviewTotals: 'Review totals →',
    confirmButton: '✓ Confirm and Save Encomienda',
    backToEdit: '← Back to edit',
    successMsg: '✓ Encomienda {id} saved successfully',
    totalItems: 'Total items',
    totalFOB: 'FOB Value',
    estimatedTax: 'Est. Tax (14%)',
    encomiendaId: 'Encomienda ID',
    sender: 'Sender',
    totalPackages: 'Packages',
    totalFOBLabel: 'Total FOB',
    totalTax: 'Total Tax',
    nuevo: 'NEW',
    usado: 'USED',
    grandTotals: 'Grand Totals',
    loadingReview: 'Loading review...',
    noData: 'No data found.',
    itemsCount: 'items',
    saveForLater: 'Save and continue later',
    savedDraftMsg: '✓ Draft saved. Resume the encomienda from the panel above.',
    updateButton: '✓ Update Encomienda',
    packageItems: 'ITEMS',
    cajasTotal: 'Total',
    cajas: 'boxes',
    lbs: 'lbs',
    cajasRegistradas: 'REGISTERED BOXES',
    paqueteLabel: 'Package',
    addPackage: '+ Add Package',
  },
};

const DRAFT_KEY = 'encomienda_draft';

// Mantener en sincronía con mapItemAgency (agencyMapper.js): todo campo nuevo
// entra acá y en el mapper en la misma edición.
const emptyItem = () => ({
  tempId: `i-${Date.now()}-${Math.random()}`,
  id: null,
  quantity: 1,
  description: '',
  description_raw: '',
  condition: 'NUEVO',
  unitValue: '',
  unit_type: 'POR_UNIDAD',
  unidad_medida: 'UNIDAD',
  catalog_id: null,
  cip_usd: null,
  pending_review: false,
  is_merchandise: true,
  catalogo_unit_type: null,
  catalogo_unidad_medida: null,
  catalogo_descripcion: null,
  source: 'manual',
});

// Keywords that suggest clothing/footwear — triggers USADO substitution hint
const CLOTHING_KEYWORDS = [
  'CAMISA','PANTALON','VESTIDO','BLUSA','ROPA','ZAPATO','TENIS',
  'BOTA','CALCETÍN','CALCETINES','SUDADERA','CHUMPA','BOXER',
];

const sumQty = (items) =>
  (items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);

// ── PackageCard (Step 2) — weight removed, now at box level ──────────────────
const PackageCard = ({
  recipientName, packageNumber, canDelete, onDelete,
  pkg, pkgId,
  encomiendaPublicId,
  wtr, onAddItem, onRemoveItem, onUpdateItem, onItemBlur, onBatchUpdateItem,
  onAnalyzeReceipt, isAnalyzing,
}) => {
  const [expanded, setExpanded] = useState(true);
  /* eslint-disable */
  useEffect(() => {
    if (isAnalyzing) setExpanded(true);
  }, [isAnalyzing]);
  /* eslint-enable */
  const [receiptUrl, setReceiptUrl] = useState(pkg?.receiptUrl || null);
  const [receiptCount, setReceiptCount] = useState(0);
  const { tenant } = useTenant();
  /* eslint-disable */
  useEffect(() => {
    if (!tenant) return;
    if (!pkgId) return;
    supabase
      .from('package_receipts')
      .select('id', { count: 'exact', head: true })
      .eq('encomienda_package_id', pkgId)
      .then(({ count }) => setReceiptCount(count || 0));
  }, [tenant, pkgId]);
  /* eslint-enable */
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptError, setReceiptError] = useState('');
  const [photoUrl, setPhotoUrl] = useState(pkg?.photoUrl || null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());

  const handleReceiptUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (receiptCount >= 20) {
      setReceiptError('Máximo 20 recibos por paquete. / Max 20 receipts per package.');
      e.target.value = '';
      return;
    }

    const remaining = 20 - receiptCount;
    const toUpload = files.slice(0, remaining);

    setReceiptError('');
    setReceiptUploading(true);

    let lastUrl = null;
    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) {
        setReceiptError('Archivo muy grande. Máximo 5MB. / File too large. Max 5MB.');
        continue;
      }
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const fileName = `${encomiendaPublicId || pkgId}-receipt-${Date.now()}.${ext}`;
        await supabase.storage.from('package-photos').upload(fileName, file);
        const { data: urlData } = supabase.storage
          .from('package-photos')
          .getPublicUrl(fileName);
        await supabase.from('package_receipts').insert({
          encomienda_package_id: pkgId,
          receipt_url: urlData.publicUrl,
          uploaded_by: 'staff',
          source: 'staff',
        });
        lastUrl = urlData.publicUrl;
      } catch (err) {
        setReceiptError(err.message);
      }
    }

    // Legacy: keep encomienda_packages.receipt_url pointing at most recent upload
    if (lastUrl) {
      try {
        await supabase
          .from('encomienda_packages')
          .update({ receipt_url: lastUrl })
          .eq('id', pkgId);
        setReceiptUrl(lastUrl);
      } catch (legacyErr) {
        console.warn('[EncWizard] legacy receipt_url update failed (non-blocking):', legacyErr);
      }
    }

    // Refresh count
    const { count: newCount } = await supabase
      .from('package_receipts')
      .select('id', { count: 'exact', head: true })
      .eq('encomienda_package_id', pkgId);
    setReceiptCount(newCount || 0);

    setReceiptUploading(false);
    e.target.value = '';
  };

  const handlePackagePhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('La foto no puede exceder 5MB');
      return;
    }
    setPhotoUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${encomiendaPublicId}-PK${packageNumber}-photo-${Date.now()}.${ext}`;
      await supabase.storage.from('package-photos').upload(fileName, file);
      const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(fileName);
      await supabase.from('encomienda_packages').update({ photo_url: urlData.publicUrl }).eq('id', pkgId);
      setPhotoUrl(urlData.publicUrl);
    } catch (err) {
      console.error('Photo upload error:', err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const items = pkg?.items || [];
  const pkgTotal = items.reduce(
    (s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitValue) || 0), 0
  );

  if (!pkgId) return null;

  return (
    <div className="tile" style={{ padding: 0, borderRadius: 'var(--radius-lg)' }}>
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition"
        onClick={() => setExpanded(e => !e)}
        style={{ overflow: 'hidden', borderRadius: 'inherit' }}
      >
        <div className="flex items-center gap-3">
          <span className="badge badge-enc">
            {wtr.paqueteLabel} {packageNumber}
          </span>
          <span className="tile-title">{recipientName || 'Destinatario'}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="badge badge-cas">
            {sumQty(items)} {wtr.itemsCount}
          </span>
          <span className="metric-value" style={{ fontSize: '16px', marginTop: 0 }}>${pkgTotal.toFixed(2)}</span>
          {canDelete && (
            <button type="button" onClick={e => { e.stopPropagation(); onDelete(); }}
              className="btn-ghost" style={{ padding: '4px', color: 'var(--avianca-red)' }}>
              <X size={14} />
            </button>
          )}
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t p-4 space-y-4" style={{ borderColor: 'var(--border)' }}>
          {/* Items list — no weight field */}
          <div className="space-y-3">
            {items.map(item => {
              const isPeso = item.unit_type === 'POR_PESO';
              const isVolumen = item.unit_type === 'POR_VOLUMEN';
              const rowTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitValue) || 0);
              const showClothingSuggestion =
                item.condition === 'USADO' &&
                CLOTHING_KEYWORDS.some(kw => (item.description || '').toUpperCase().includes(kw)) &&
                !dismissedSuggestions.has(item.tempId);

              return (
                <div key={item.tempId} className="space-y-2">
                  <div
                    className="grid grid-cols-2 gap-2 [grid-template-areas:'q_c'_'d_d'_'u_t'_'x_x'] sm:grid-cols-[60px_1.6fr_106px_84px_88px_22px] sm:gap-3 sm:items-start sm:[grid-template-areas:'q_d_c_u_t_x']"
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px',
                      background: item.pending_review ? 'var(--amber-soft)' : undefined,
                    }}
                  >
                    <div style={{ gridArea: 'q' }}>
                      <p className="metric-label sm:hidden mb-1">{wtr.itemQty}</p>
                      {item.pending_review && (
                        <div className="badge badge-hold w-full mb-1 text-center" style={{ fontSize: '8px' }}>
                          ⚠ Pend. revisión
                        </div>
                      )}
                      {isPeso ? (
                        <div>
                          <p className="metric-sub text-center mb-1">
                            {item.unidad_medida || 'PESO'}
                          </p>
                          <input
                            type="number" min={0} step={0.01}
                            className="input text-center"
                            style={{ width: '60px', padding: '4px' }}
                            placeholder="0.0"
                            value={item.quantity}
                            onChange={e => onUpdateItem(item.tempId, 'quantity', e.target.value)}
                            onBlur={() => onItemBlur(item)}
                          />
                        </div>
                      ) : (
                        <div>
                          <input
                            type="number" min={1}
                            className="input text-center"
                            style={{ width: '60px', padding: '4px' }}
                            value={item.quantity}
                            onChange={e => onUpdateItem(item.tempId, 'quantity', e.target.value)}
                            onBlur={() => onItemBlur(item)}
                          />
                          {isVolumen && item.unidad_medida && (
                            <p className="metric-sub text-center mt-1">
                              {item.unidad_medida}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ gridArea: 'd' }}>
                      <p className="metric-label sm:hidden mb-1">{wtr.itemDesc}</p>
                      <CatalogSearch
                        value={item.description}
                        placeholder={wtr.itemDesc}
                        onChange={val => onUpdateItem(item.tempId, 'description', val)}
                        onBlur={() => onItemBlur(item)}
                        onSelect={catalogItem => {
                          const merged = {
                            description: catalogItem.descripcion,
                            description_raw: item.description_raw || item.description || '',
                            unitValue: catalogItem.fob_usd != null ? String(catalogItem.fob_usd) : item.unitValue,
                            unit_type: catalogItem.unit_type || 'POR_UNIDAD',
                            unidad_medida: catalogItem.unidad_medida || 'UNIDAD',
                            catalog_id: catalogItem.id,
                            cip_usd: catalogItem.cip_usd,
                          };
                          onBatchUpdateItem(item.tempId, merged);
                          onItemBlur({ ...item, ...merged });
                        }}
                      />
                    </div>

                    <div style={{ gridArea: 'c' }}>
                      <p className="metric-label sm:hidden mb-1">{wtr.itemCondition}</p>
                      <div className="flex gap-1">
                        <button type="button"
                          onClick={() => { onUpdateItem(item.tempId, 'condition', 'NUEVO'); onItemBlur({ ...item, condition: 'NUEVO' }); }}
                          className={`btn ${item.condition === 'NUEVO' ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ flex: 1, padding: '4px', fontSize: '10px', justifyContent: 'center' }}
                        >{wtr.nuevo}</button>
                        <button type="button"
                          onClick={() => { onUpdateItem(item.tempId, 'condition', 'USADO'); onItemBlur({ ...item, condition: 'USADO' }); }}
                          className={`btn ${item.condition === 'USADO' ? 'btn-secondary' : 'btn-secondary'}`}
                          style={{ flex: 1, padding: '4px', fontSize: '10px', justifyContent: 'center', background: item.condition === 'USADO' ? 'var(--text-secondary)' : '', color: item.condition === 'USADO' ? '#fff' : '' }}
                        >{wtr.usado}</button>
                      </div>
                    </div>

                    <div style={{ gridArea: 'u' }}>
                      <p className="metric-label sm:hidden mb-1">{wtr.itemUnitValue}</p>
                      <input
                        type="number" min={0} step={0.01}
                        className="input text-right w-full"
                        style={{ padding: '4px' }}
                        placeholder="0.00"
                        value={item.unitValue}
                        onChange={e => onUpdateItem(item.tempId, 'unitValue', e.target.value)}
                        onBlur={() => onItemBlur(item)}
                      />
                    </div>

                    <div style={{ gridArea: 't' }}>
                      <p className="metric-label sm:hidden mb-1">{wtr.itemTotal}</p>
                      <div className="text-right font-mono font-bold text-sm whitespace-nowrap" style={{ color: 'var(--green)' }}>
                        ${rowTotal.toFixed(2)}
                      </div>
                    </div>

                    <div style={{ gridArea: 'x' }} className="flex items-start justify-end sm:justify-center">
                      <button type="button" onClick={() => onRemoveItem(item.tempId)}
                        className="btn-ghost" style={{ padding: '4px', color: 'var(--avianca-red)' }}>
                        <X size={13} />
                      </button>
                    </div>
                  </div>

                  {showClothingSuggestion && (
                    <div className="badge badge-hold w-full" style={{ padding: '8px 12px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', borderRadius: 'var(--radius-md)' }}>
                      <p style={{ fontSize: '11px', flex: 1, margin: 0, fontWeight: '500', whiteSpace: 'normal' }}>
                        💡 Para ropa/zapatos usados, considera usar 'ROPA USADA EN GENERAL' o 'ZAPATOS USADOS EN GENERAL' que se declaran por KILOGRAMO
                      </p>
                      <div className="flex gap-2 shrink-0 items-center">
                        <button
                          type="button"
                          onClick={async () => {
                            const isShoes = ['ZAPATO','TENIS','BOTA'].some(kw => (item.description || '').toUpperCase().includes(kw));
                            const searchTerm = isShoes ? 'ZAPATOS USADOS' : 'ROPA USADA EN GENERAL';
                            const { data: catItem } = await supabase
                              .from('customs_catalog')
                              .select('id, descripcion, unidad_medida, unit_type, fob_usd, cip_usd')
                              .ilike('descripcion', searchTerm + '%')
                              .limit(1)
                              .maybeSingle();
                            if (catItem) {
                              const merged = {
                                description: catItem.descripcion,
                                unitValue: catItem.fob_usd != null ? String(catItem.fob_usd) : item.unitValue,
                                unit_type: catItem.unit_type || 'POR_PESO',
                                unidad_medida: catItem.unidad_medida || 'KILOGRAMO',
                                catalog_id: catItem.id,
                                cip_usd: catItem.cip_usd,
                              };
                              onBatchUpdateItem(item.tempId, merged);
                              onItemBlur({ ...item, ...merged });
                            }
                            setDismissedSuggestions(prev => new Set([...prev, item.tempId]));
                          }}
                          className="btn btn-primary"
                          style={{ fontSize: '9px', padding: '4px 8px' }}
                        >
                          Cambiar
                        </button>
                        <button
                          type="button"
                          onClick={() => setDismissedSuggestions(prev => new Set([...prev, item.tempId]))}
                          className="btn-ghost"
                          style={{ padding: '2px' }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button type="button" onClick={onAddItem}
            className="btn btn-ghost" style={{ color: 'var(--avianca-red)', fontWeight: 'bold' }}>
            <Plus size={13} /> {wtr.addItem}
          </button>

          {/* Receipt upload */}
          <div className="pt-2 border-t border-gray-100" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2 flex-wrap">
              {receiptCount > 0 ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="badge badge-done flex items-center gap-1">
                    <CheckCircle size={12} /> {receiptCount} recibo(s)
                  </span>
                  {receiptUrl && (
                    <a href={receiptUrl} target="_blank" rel="noreferrer"
                      className="btn btn-ghost" style={{ color: 'var(--blue-text)' }}>
                      <ExternalLink size={11} /> Ver último
                    </a>
                  )}
                  {receiptCount < 20 && (
                    <label className="btn btn-ghost" style={{ color: 'var(--amber)', cursor: 'pointer' }}>
                      + Agregar
                      <input type="file" className="hidden" multiple accept="image/jpeg,image/png,application/pdf" onChange={handleReceiptUpload} disabled={receiptUploading} />
                    </label>
                  )}
                </div>
              ) : (
                <label className="btn btn-secondary" style={{ color: 'var(--avianca-red)', borderColor: 'var(--avianca-red)', cursor: 'pointer' }}>
                  {receiptUploading ? 'Subiendo...' : '📎 Subir Recibo'}
                  <input type="file" className="hidden" multiple accept="image/jpeg,image/png,application/pdf" onChange={handleReceiptUpload} disabled={receiptUploading} />
                </label>
              )}
              {receiptError && (
                <p className="text-red-500 text-[10px] mt-0.5">{receiptError}</p>
              )}
              {onAnalyzeReceipt && (
                <button
                  type="button"
                  onClick={onAnalyzeReceipt}
                  className="btn btn-secondary"
                  style={{ color: 'var(--blue-text)', borderColor: 'var(--blue)' }}
                >
                  🔍 Analizar recibo con IA
                </button>
              )}
            </div>
          </div>

          {/* Package photo */}
          <div style={{ marginTop: '8px' }}>
            {photoUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>📷 Foto del paquete:</span>
                <a
                  href={photoUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: '11px', color: 'var(--sky-blue)', textDecoration: 'underline' }}
                >
                  Ver foto
                </a>
                <label style={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}>
                  Cambiar
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    style={{ display: 'none' }}
                    onChange={e => e.target.files[0] && handlePackagePhotoUpload(e.target.files[0])}
                  />
                </label>
              </div>
            ) : (
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'var(--sky-blue)',
                background: 'var(--tile)',
              }}>
                {photoUploading ? 'Subiendo...' : '📷 Adjuntar foto del paquete'}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && handlePackagePhotoUpload(e.target.files[0])}
                  disabled={photoUploading}
                />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Phone normalization ──────────────────────────────────────────────────────
// Stored format is digits INCLUDING country code, e.g. 13017927456 / 50372000238.
// Postgres derives phone_key from this by stripping a leading 1 or 503 on
// 11-digit strings, so React and the DB agree without duplicating that logic.
const PHONE_COUNTRIES = [
  { code: '1',   label: '+1',   name: 'USA' },
  { code: '503', label: '+503', name: 'El Salvador' },
];

// Joins a country code with a national number, stripping any punctuation and
// any country code the operator retyped into the national field.
function composePhone(countryCode, national) {
  const nat = String(national || '').replace(/\D/g, '');
  if (!nat) return '';
  const cc = String(countryCode || '').replace(/\D/g, '');
  if (!cc) return nat;
  return nat.startsWith(cc) && nat.length > cc.length ? nat : cc + nat;
}

// Splits a stored number back into { country, national } for display.
// Falls back to the supplied default when no known code is detected.
function decomposePhone(stored, fallbackCode = '1') {
  const d = String(stored || '').replace(/\D/g, '');
  if (!d) return { country: fallbackCode, national: '' };
  if (d.length === 11 && d.startsWith('503')) return { country: '503', national: d.slice(3) };
  if (d.length === 11 && d.startsWith('1'))   return { country: '1',   national: d.slice(1) };
  if (d.length === 8)                          return { country: '503', national: d };
  if (d.length === 10)                         return { country: '1',   national: d };
  return { country: fallbackCode, national: d };
}

// ── EncomiendaWizard ──────────────────────────────────────────────────────────
const EncomiendaWizard = ({ currentManifest, user, resumeId, onResumeConsumed, editMode = false, encomiendaId = null, onComplete, onClose }) => {
  const [lang, setLang] = useState('es');
  const wtr = wt[lang];
  const [currentStep, setCurrentStep] = useState(1);
  const [draftBanner, setDraftBanner] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState(null);

  // Step 1 state
  const [encomiendaDbId, setEncomiendaDbId] = useState(null);
  const [encomiendaPublicId, setEncomiendaPublicId] = useState(null);
  const [senderPhone, setSenderPhone] = useState('');
  const [senderName, setSenderName] = useState('');
  const [customerId, setCustomerId] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [customerBadge, setCustomerBadge] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [senderCountry, setSenderCountry] = useState('1');
  const [senderMatches, setSenderMatches] = useState([]);
  const [showSenderMatches, setShowSenderMatches] = useState(false);

  // Single recipient
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientCountry, setRecipientCountry] = useState('503');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [recipientDepartamento, setRecipientDepartamento] = useState('');
  const [recipientMunicipio, setRecipientMunicipio] = useState('');
  const [deliveryType, setDeliveryType] = useState('Drop Off');
  const [savedDestinatarios, setSavedDestinatarios] = useState([]);
  const [selectedDestinatarioId, setSelectedDestinatarioId] = useState('');
  const [destinatarioPickedFromBank, setDestinatarioPickedFromBank] = useState(false);
  const [, setPackageDbId] = useState(null);
  // Ordered list of DB package UUIDs for Step 2 (multi-package)
  const [pkgIds, setPkgIds] = useState([]);
  const [addingPackage, setAddingPackage] = useState(false);

  const [step1Saving, setStep1Saving] = useState(false);
  const [step1Error, setStep1Error] = useState('');
  const [step1Success, setStep1Success] = useState(false);

  // Step 2 state
  const [cajas, setCajas] = useState([
    {
      localId: `caja-${Date.now()}`,
      dbBoxId: null,           // populated after save; matches boxes.box_id
      caja_number: 1,
      weight_lb: '',
      dimensions: '',
      packageIds: [],          // encomienda_packages.id UUIDs assigned to this caja
      saved: false,
      saving: false,
    },
  ]);
  const [packages, setPackages] = useState({}); // keyed by packageDbId: { items, receiptUrl }
  const [analyzingPackageId, setAnalyzingPackageId] = useState(null);

  // Step 3 state
  const [reviewData, setReviewData] = useState(null);
  const [reviewBoxes, setReviewBoxes] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [saveForLaterMsg, setSaveForLaterMsg] = useState('');

  const inFlightItemInserts = useRef(new Set());
  // Promesas de INSERT/UPDATE de artículos en vuelo (el flush de agencia las espera).
  const inFlightItemSaves = useRef(new Set());
  // Destinatario del paso 1 tal como quedó en la base la última vez (agencia,
  // modo edición): sólo los paquetes que todavía lo tienen se actualizan.
  const step1DefaultRef = useRef(null);
  const isAgency = useIsAgencyTenant();

  // Running totals (computed from packages state)
  const allItems = Object.values(packages).flatMap(p => p.items || []);
  const totalItemCount = sumQty(allItems);
  const totalFOB = allItems.reduce(
    (s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitValue) || 0), 0
  );
  const estimatedTax = allItems
    .filter(it => it.condition === 'NUEVO')
    .reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitValue) || 0) * 0.14, 0);
  const totalBoxWeight = cajas.reduce((s, c) => s + (parseFloat(c.weight_lb) || 0), 0);
  const { tenant } = useTenant();

  // ── On mount ────────────────────────────────────────────────────────────────
  /* eslint-disable */
  useEffect(() => {
    if (!tenant) return;
    if (editMode && encomiendaId) {
      loadDraft(encomiendaId);
      return;
    }
    if (resumeId) {
      loadDraft(resumeId);
      if (onResumeConsumed) onResumeConsumed();
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.encomiendaId) {
          setSavedDraftId(d.encomiendaId);
          setDraftBanner(true);
        }
      }
    } catch (_) {}
  }, [tenant, resumeId, editMode, encomiendaId]);
  /* eslint-enable */

  // ── Draft helpers ────────────────────────────────────────────────────────────
  const saveDraftLocally = (encId, publicId) => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ encomiendaId: encId, encomiendaPublicId: publicId }));
  };

  // Lectura de artículos: en agencia se embebe el catálogo para derivar
  // unit_type; los demás tenants leen exactamente lo de siempre.
  const ITEM_SELECT = isAgency ? '*, customs_catalog ( descripcion, unidad_medida, unit_type )' : '*';

  // Destinatario del paso 1 en la forma de sesión (agencia lo usa como default).
  const defaultRecipient = {
    name: recipientName.trim(),
    phone: composePhone(recipientCountry, recipientPhone) || '',
    address: recipientAddress.trim(),
    departamento: recipientDepartamento || '',
    municipio: recipientMunicipio || '',
    delivery_type: deliveryType,
  };

  const loadDraft = async (id) => {
    const { data: enc } = await supabase
      .from('encomiendas')
      .select(`*, customers(id, full_name, phone_number), encomienda_packages(*)`)
      .eq('id', id)
      .maybeSingle();

    if (!enc) return;

    setEncomiendaDbId(enc.id);
    setEncomiendaPublicId(enc.unique_encomienda_id);
    setCustomerId(enc.customer_id);
    setSenderName(enc.customers?.full_name || '');
    const sp = decomposePhone(enc.customers?.phone_number, '1');
    setSenderCountry(sp.country);
    setSenderPhone(sp.national);

    // Restore recipient from first package; restore all packages for Step 2
    const pkgs = (enc.encomienda_packages || []).sort((a, b) => a.package_number - b.package_number);
    const firstPkg = pkgs[0];
    if (firstPkg) {
      setRecipientName(firstPkg.recipient_name || '');
      const rp = decomposePhone(firstPkg.recipient_phone, '503');
      setRecipientCountry(rp.country);
      setRecipientPhone(rp.national);
      setRecipientAddress(firstPkg.recipient_address || '');
      setDeliveryType(firstPkg.delivery_type || 'Drop Off');
      // Departamento/Municipio: prefer explicit columns; fallback to fuzzy match
      // against the joined recipient_address for legacy records.
      let dept = firstPkg.recipient_departamento || '';
      let mun = firstPkg.recipient_municipio || '';
      if (!dept && firstPkg.recipient_address) {
        const addr = firstPkg.recipient_address.toLowerCase();
        const match = SV_DEPARTAMENTOS.find(d => addr.includes(d.name.toLowerCase()));
        if (match) {
          dept = match.name;
          if (!mun) {
            const munMatch = match.municipios.find(m => addr.includes(m.name.toLowerCase()));
            if (munMatch) mun = munMatch.name;
          }
        }
      }
      setRecipientDepartamento(dept);
      setRecipientMunicipio(mun);
      if (dept) console.log('[EncomiendaWizard] recipient geo hydrated:', { dept, mun, source: firstPkg.recipient_departamento ? 'db' : 'fuzzy' });
      setPackageDbId(firstPkg.id);
      step1DefaultRef.current = recipientDesdePaqueteRow(firstPkg);
    }

    // Load items for all packages (each package keeps its own recipient)
    const pkgState = {};
    const loadedPkgIds = [];
    for (const p of pkgs) {
      const { data: items } = await supabase
        .from('encomienda_items')
        .select(ITEM_SELECT)
        .eq('package_id', p.id)
        .order('created_at', { ascending: true });

      const pkgItems = (items || []).map(mapItemAgency);

      pkgState[p.id] = {
        receiptUrl: p.receipt_url || null,
        photoUrl: p.photo_url || null,
        items: pkgItems.length > 0 ? pkgItems : [emptyItem()],
        recipient: recipientDesdePaqueteRow(p),
        package_number: p.package_number,
      };
      loadedPkgIds.push(p.id);
    }
    setPackages(pkgState);
    setPkgIds(loadedPkgIds);

    // Restore cajas from boxes (real box rows linked via encomienda_id)
    const { data: boxData } = await supabase
      .from('boxes')
      .select('box_id, weight_lb, dimensions, box_packages(package_ref_id)')
      .eq('encomienda_id', enc.id)
      .eq('tenant_id', tenant?.id)
      .order('created_at', { ascending: true });

    if (boxData && boxData.length > 0) {
      setCajas(boxData.map((b, i) => ({
        localId: `caja-${b.box_id}`,
        dbBoxId: b.box_id,
        caja_number: i + 1,
        weight_lb: b.weight_lb?.toString() || '',
        dimensions: b.dimensions || '',
        packageIds: (b.box_packages || []).map(bp => bp.package_ref_id),
        saved: true,
        saving: false,
      })));
    }

    setCurrentStep(2);
    setDraftBanner(false);
  };

  const continueDraft = () => { if (savedDraftId) loadDraft(savedDraftId); setDraftBanner(false); };
  const discardDraft = () => { localStorage.removeItem(DRAFT_KEY); setSavedDraftId(null); setDraftBanner(false); };

  // ── Step 1: Destinatario bank ────────────────────────────────────────────────
  useEffect(() => {
    if (!customerId) {
      setSavedDestinatarios([]);
      setSelectedDestinatarioId('');
      setDestinatarioPickedFromBank(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('customer_addresses')
        .select('*')
        .eq('customer_id', customerId)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        console.error('[EncomiendaWizard] fetch savedDestinatarios error:', error);
        return;
      }
      setSavedDestinatarios(data || []);
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const applyDestinatario = (addr) => {
    if (!addr) return;
    setRecipientName(addr.recipient_name || '');
    const rp = decomposePhone(addr.recipient_phone, '503');
    setRecipientCountry(rp.country);
    setRecipientPhone(rp.national);
    setRecipientAddress(addr.direccion || '');
    setRecipientDepartamento(addr.departamento || '');
    setRecipientMunicipio(addr.municipio || '');
    // delivery_type intentionally not applied — user picks manually
  };

  const upsertNewDestinatarioIfNeeded = async () => {
    // Only upsert if user did NOT pick from the bank (fresh manual entry)
    if (destinatarioPickedFromBank) return;
    if (!customerId) return;
    if (!recipientName.trim()) return; // guard against blanks

    // Deduplication check: if a customer_addresses row already exists with same
    // recipient_name + direccion + departamento + municipio, skip the upsert.
    const { data: existing } = await supabase
      .from('customer_addresses')
      .select('id')
      .eq('customer_id', customerId)
      .eq('tenant_id', tenant?.id)
      .ilike('recipient_name', recipientName.trim())
      .ilike('direccion', recipientAddress.trim() || '')
      .eq('departamento', recipientDepartamento || '')
      .eq('municipio', recipientMunicipio || '')
      .maybeSingle();

    if (existing) {
      console.log('[EncomiendaWizard] destinatario already in bank, skipping upsert:', existing.id);
      return;
    }

    // Auto-generate a label from the recipient name — user can rename later via AddressManagerModal
    const autoLabel = recipientName.trim().slice(0, 40) || 'Dirección';

    const { data: inserted, error } = await supabase
      .from('customer_addresses')
      .insert([{
        customer_id: customerId,
        tenant_id: tenant?.id,
        label: autoLabel,
        recipient_name: recipientName.trim(),
        recipient_phone: composePhone(recipientCountry, recipientPhone) || '',
        direccion: recipientAddress.trim() || '',
        departamento: recipientDepartamento || '',
        municipio: recipientMunicipio || '',
        reference_point: null,
        is_default: false,
      }])
      .select()
      .single();

    if (error) {
      console.error('[EncomiendaWizard] upsert destinatario failed:', error);
      return;
    }
    console.log('[EncomiendaWizard] new destinatario added to bank:', inserted?.id);
  };

  // ── Step 1: Fuzzy sender search ──────────────────────────────────────────────
  // Substring match on phone_digits so a stored "+1 (301) 792-7456" is reachable
  // by typing any fragment. Returns up to 8 candidates rather than one row —
  // .maybeSingle() threw whenever a number matched multiple profiles.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const nat = senderPhone.replace(/\D/g, '');
    if (nat.length < 4 || !tenant?.id) {
      setSenderMatches([]);
      setShowSenderMatches(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookingUp(true);
      const { data, error } = await supabase
        .from('customers')
        .select('id, full_name, phone_number, phone_digits')
        .eq('tenant_id', tenant.id)
        .is('deleted_at', null)
        .ilike('phone_digits', '%' + nat + '%')
        .limit(8);
      if (cancelled) return;
      setLookingUp(false);
      if (error) {
        console.error('[EncomiendaWizard] sender search error:', error);
        setSenderMatches([]);
        return;
      }
      const rows = data || [];
      setSenderMatches(rows);
      setShowSenderMatches(rows.length > 0);
      if (rows.length === 0) {
        setIsNewCustomer(true);
        setCustomerBadge('new');
        setCustomerId(null);
      } else {
        setCustomerBadge(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [senderPhone, senderCountry, tenant?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const pickSenderMatch = (row) => {
    const { country, national } = decomposePhone(row.phone_number, senderCountry);
    setSenderCountry(country);
    setSenderPhone(national);
    setSenderName(row.full_name || '');
    setCustomerId(row.id);
    setIsNewCustomer(false);
    setCustomerBadge('existing');
    setSenderMatches([]);
    setShowSenderMatches(false);
  };

  // ── Step 1: Save to DB ───────────────────────────────────────────────────────
  const handleStep1Save = async () => {
    if (!senderName.trim()) {
      setStep1Error(lang === 'es' ? 'El nombre del remitente es obligatorio.' : 'Sender name is required.');
      return;
    }
    if (!senderPhone.trim()) {
      setStep1Error(lang === 'es' ? 'El teléfono del remitente es obligatorio.' : 'Sender phone is required.');
      return;
    }
    if (!recipientName.trim()) {
      setStep1Error(lang === 'es' ? 'El nombre del destinatario es obligatorio.' : 'Recipient name is required.');
      return;
    }
    if (!recipientAddress.trim()) {
      setStep1Error(lang === 'es' ? 'La dirección del destinatario es obligatoria.' : 'Recipient address is required.');
      return;
    }

    // Edit mode: update existing packages with current recipient fields, then advance
    if (editMode && encomiendaDbId) {
      try {
        const sufijoGeo = [recipientDepartamento.trim(), recipientMunicipio.trim()].filter(Boolean).join(', ');
        // Agencia: la dirección hidratada desde la fila ya trae ", depto, muni";
        // no se vuelve a sufijar. Los demás tenants conservan la unión de siempre.
        const joinedAddress = isAgency && sufijoGeo && recipientAddress.trim().toLowerCase().endsWith(sufijoGeo.toLowerCase())
          ? recipientAddress.trim()
          : [recipientAddress.trim(), recipientDepartamento.trim(), recipientMunicipio.trim()].filter(Boolean).join(', ');
        const recipientPatch = {
          recipient_name: recipientName.trim(),
          recipient_phone: composePhone(recipientCountry, recipientPhone) || null,
          recipient_address: joinedAddress,
          recipient_departamento: recipientDepartamento || null,
          recipient_municipio: recipientMunicipio || null,
          delivery_type: deliveryType,
        };
        if (isAgency) {
          // Agencia: sólo los paquetes que todavía llevan el destinatario por
          // defecto anterior; los que tienen destinatario propio lo conservan.
          const previo = step1DefaultRef.current;
          const nuevo = recipientDesdePaqueteRow(recipientPatch);
          await agencyApi.actualizarDestinatarioPorDefecto({ encomiendaId: encomiendaDbId, previo, nuevo });
          setPackages(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(id => {
              const r = next[id]?.recipient;
              if (!previo || (r && mismoRecipient(r, previo))) next[id] = { ...next[id], recipient: nuevo };
            });
            return next;
          });
          step1DefaultRef.current = nuevo;
        } else {
          await supabase.from('encomienda_packages')
            .update(recipientPatch)
            .eq('encomienda_id', encomiendaDbId);
        }
        await supabase.from('encomiendas')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', encomiendaDbId);
        console.log('[EncomiendaWizard] edit-mode Step1 saved:', { encomiendaDbId, recipientName: recipientName.trim(), deliveryType });
        await upsertNewDestinatarioIfNeeded();
        setCurrentStep(2);
        return;
      } catch (err) {
        alert('Error guardando destinatario: ' + err.message);
        return;
      }
    }

    // If already saved (non-edit mode), just advance
    if (encomiendaDbId) {
      setCurrentStep(2);
      return;
    }

    setStep1Saving(true);
    setStep1Error('');

    try {
      // 1. Customer lookup or create
      let finalCustomerId = customerId;
      if (isNewCustomer || !finalCustomerId) {
        const clean = composePhone(senderCountry, senderPhone);
        const { data: existing } = await supabase
          .from('customers')
          .select('id')
          .eq('tenant_id', tenant?.id)
          .is('deleted_at', null)
          .ilike('phone_digits', '%' + senderPhone.replace(/\D/g, '') + '%')
          .limit(1);

        if (existing?.[0]) {
          finalCustomerId = existing[0].id;
          setCustomerId(existing[0].id);
        } else {
          const nameParts = senderName.trim().split(/\s+/);
          let newId = ((nameParts[0]?.[0] || 'X') + (nameParts[1]?.[0] || 'X')).toUpperCase();
          const { data: idConflict } = await supabase.from('customers').select('id').eq('id', newId).maybeSingle();
          if (idConflict) newId = newId + Date.now().toString().slice(-3);

          const { error: custErr } = await supabase.from('customers').insert([{
            id: newId,
            full_name: senderName.trim(),
            phone_number: clean,
            tenant_id: tenant?.id,
          }]);
          if (custErr) throw custErr;
          finalCustomerId = newId;
          setCustomerId(newId);
        }
      }

      // 2. Generate encomienda ID
      const { data: encId, error: rpcErr } = await supabase.rpc('generate_encomienda_id');
      if (rpcErr || !encId) throw new Error('Failed to generate encomienda ID.');

      // 3. Resolve manifest ID
      let manifestId = currentManifest?.id || null;
      if (!manifestId) {
        const { data: openMf } = await supabase.from('manifests').select('id').eq('status', 'OPEN').maybeSingle();
        manifestId = openMf?.id || null;
      }

      // 4. Insert encomienda record
      const { data: encRow, error: encErr } = await supabase
        .from('encomiendas')
        .insert([{ unique_encomienda_id: encId, customer_id: finalCustomerId, status: 'DRAFT', manifest_id: manifestId }])
        .select()
        .single();
      if (encErr) throw encErr;

      const dbId = encRow.id;
      setEncomiendaDbId(dbId);
      setEncomiendaPublicId(encId);

      // 5. Insert single recipient as one encomienda_package
      const { data: pkgRow, error: pkgErr } = await supabase
        .from('encomienda_packages')
        .insert([{
          encomienda_id: dbId,
          recipient_name: recipientName.trim(),
          recipient_phone: composePhone(recipientCountry, recipientPhone) || null,
          recipient_address: [recipientAddress.trim(), recipientDepartamento.trim(), recipientMunicipio.trim()].filter(Boolean).join(', '),
          recipient_departamento: recipientDepartamento || null,
          recipient_municipio: recipientMunicipio || null,
          delivery_type: deliveryType,
          package_number: 1,
          entry_manifest_id: manifestId,
        }])
        .select()
        .single();
      if (pkgErr) throw pkgErr;

      await upsertNewDestinatarioIfNeeded();

      setPackageDbId(pkgRow.id);
      setPkgIds([pkgRow.id]);
      step1DefaultRef.current = recipientDesdePaqueteRow(pkgRow);
      setPackages({ [pkgRow.id]: { items: [emptyItem()], photoUrl: null, recipient: recipientDesdePaqueteRow(pkgRow), package_number: 1 } });
      saveDraftLocally(dbId, encId);

      setStep1Success(true);
      setTimeout(() => {
        setStep1Success(false);
        setCurrentStep(2);
      }, 1400);
    } catch (err) {
      setStep1Error(err.message || 'Error al guardar.');
    } finally {
      setStep1Saving(false);
    }
  };

  // ── Step 2: Caja (real box) helpers ─────────────────────────────────────────
  const saveCaja = async (localId) => {
    const caja = cajas.find(c => c.localId === localId);
    if (!caja) return;

    // Validation: weight, dimensions, and at least one package
    const weight = parseFloat(caja.weight_lb);
    if (!weight || weight <= 0) {
      alert('Ingresá el peso de la caja.');
      return;
    }
    if (!caja.dimensions?.trim()) {
      alert('Ingresá las dimensiones de la caja.');
      return;
    }
    if (!caja.packageIds || caja.packageIds.length === 0) {
      alert('Seleccioná al menos un paquete para esta caja.');
      return;
    }

    setCajas(prev => prev.map(c => c.localId === localId ? { ...c, saving: true } : c));

    try {
      // Resolve manifest
      let manifestId = currentManifest?.id || null;
      if (!manifestId) {
        const { data: openMf } = await supabase
          .from('manifests').select('id').eq('status', 'OPEN').maybeSingle();
        manifestId = openMf?.id || null;
      }

      const newBoxId = () => `BOX-${customerId}-${Date.now()}`;
      let boxIdStr = caja.dbBoxId || newBoxId();

      const boxPayload = {
        box_id: boxIdStr,
        tenant_id: tenant?.id,
        customer_id: customerId,
        encomienda_id: encomiendaDbId,
        weight_lb: weight,
        dimensions: caja.dimensions.trim(),
        recipient_name: recipientName.trim(),
        recipient_phone: composePhone(recipientCountry, recipientPhone) || null,
        recipient_address: [recipientAddress.trim(), recipientDepartamento.trim(), recipientMunicipio.trim()]
          .filter(Boolean).join(', '),
        departamento: recipientDepartamento || null,
        municipio: recipientMunicipio || null,
        delivery_type: deliveryType,
        manifest_id: manifestId,
        entry_manifest_id: manifestId,
      };

      if (caja.dbBoxId) {
        // Update existing box
        const { error: updErr } = await supabase.from('boxes')
          .update(boxPayload)
          .eq('box_id', caja.dbBoxId)
          .eq('tenant_id', tenant?.id);
        if (updErr) throw updErr;

        // Replace box_packages: delete all, re-insert selected
        const { error: delErr } = await supabase.from('box_packages')
          .delete()
          .eq('box_id', caja.dbBoxId)
          .eq('tenant_id', tenant?.id);
        if (delErr) throw delErr;
      } else {
        // Insert new box — the DB trigger numbers it; a retry may use a fresh id
        const insertedBox = await insertBox(supabase, boxPayload, { regenerateId: newBoxId });
        boxIdStr = insertedBox.box_id;
      }

      // Insert box_packages rows for selected packages
      const bpRows = caja.packageIds.map((pkgId, idx) => ({
        tenant_id: tenant?.id,
        box_id: boxIdStr,
        client_id: customerId,
        package_type: 'encomienda',
        package_ref_id: pkgId,
        is_primary: idx === 0,
      }));
      const { error: bpErr } = await supabase.from('box_packages').insert(bpRows);
      if (bpErr) throw bpErr;

      setCajas(prev => prev.map(c =>
        c.localId === localId
          ? { ...c, dbBoxId: boxIdStr, saved: true, saving: false }
          : c
      ));
      console.log('[EncomiendaWizard] caja saved:', { boxIdStr, packageCount: caja.packageIds.length });
    } catch (err) {
      console.error('[EncomiendaWizard] saveCaja failed:', err);
      alert(err?.message === BOX_NUMBER_ERROR ? BOX_NUMBER_ERROR : 'Error guardando la caja: ' + (err?.message || 'desconocido'));
      setCajas(prev => prev.map(c => c.localId === localId ? { ...c, saving: false } : c));
    }
  };

  const deleteCaja = async (localId) => {
    const caja = cajas.find(c => c.localId === localId);
    if (!caja) return;

    // If never saved, just drop locally
    if (!caja.dbBoxId) {
      setCajas(prev => {
        const filtered = prev.filter(c => c.localId !== localId);
        return filtered.length === 0
          ? [{ localId: `caja-${Date.now()}`, dbBoxId: null, caja_number: 1, weight_lb: '', dimensions: '', packageIds: [], saved: false, saving: false }]
          : filtered.map((c, i) => ({ ...c, caja_number: i + 1 }));
      });
      return;
    }

    // Guard: block only if manifest is closed or box has been dispatched to Boxful.
    // A box on an open, undispatched manifest can be deleted freely — the
    // after_box_delete trigger + guia_stops ON DELETE CASCADE clean up the guía.
    const { data: boxRow } = await supabase.from('boxes')
      .select('boxful_dispatched_at, manifest_id')
      .eq('box_id', caja.dbBoxId)
      .eq('tenant_id', tenant?.id)
      .maybeSingle();

    if (!boxRow) {
      // Box already gone from DB — remove from local state and return
      setCajas(prev => prev.filter(c => c.localId !== localId));
      return;
    }

    if (boxRow.boxful_dispatched_at) {
      alert('Esta caja ya fue despachada por Boxful. No se puede eliminar.');
      return;
    }

    if (boxRow.manifest_id) {
      const { data: manifestRow } = await supabase.from('manifests')
        .select('status')
        .eq('id', boxRow.manifest_id)
        .maybeSingle();
      if (manifestRow?.status === 'CLOSED') {
        alert('Esta caja pertenece a un manifiesto cerrado. No se puede eliminar.');
        return;
      }
    }

    // Check if any guia_stops exist so we can warn the user in the confirm dialog
    const { data: stops } = await supabase.from('guia_stops')
      .select('id')
      .eq('box_id', caja.dbBoxId)
      .eq('tenant_id', tenant?.id)
      .limit(1);

    const hasGuiaStops = (stops || []).length > 0;
    const confirmMsg = hasGuiaStops
      ? 'Esta caja está en la guía. Al eliminarla, se removerá también de la guía. ¿Continuar?'
      : '¿Eliminar esta caja? Los paquetes quedarán sin asignar y podrán ir en otra caja.';

    const confirmed = window.confirm(confirmMsg);
    if (!confirmed) return;

    const { error } = await supabase.from('boxes')
      .delete()
      .eq('box_id', caja.dbBoxId)
      .eq('tenant_id', tenant?.id);
    if (error) {
      console.error('[EncomiendaWizard] deleteCaja failed:', error);
      alert('Error eliminando la caja: ' + error.message);
      return;
    }

    setCajas(prev => {
      const filtered = prev.filter(c => c.localId !== localId);
      return filtered.length === 0
        ? [{ localId: `caja-${Date.now()}`, dbBoxId: null, caja_number: 1, weight_lb: '', dimensions: '', packageIds: [], saved: false, saving: false }]
        : filtered.map((c, i) => ({ ...c, caja_number: i + 1 }));
    });
    console.log('[EncomiendaWizard] caja deleted:', { boxId: caja.dbBoxId });
  };

  // ── Step 2: Package/item helpers ─────────────────────────────────────────────
  const addItem = (pkgId) =>
    setPackages(p => ({ ...p, [pkgId]: { ...p[pkgId], items: [...(p[pkgId]?.items || []), emptyItem()] } }));

  const removeItem = async (pkgId, tempId) => {
    const pkg = packages[pkgId];
    const target = pkg?.items?.find(it => it.tempId === tempId);

    // Remove from state first for responsive UI.
    setPackages(p => ({
      ...p,
      [pkgId]: { ...p[pkgId], items: p[pkgId].items.filter(it => it.tempId !== tempId) },
    }));

    // If persisted, delete from DB. If blank/never-persisted, nothing to do.
    if (target?.id) {
      const query = supabase.from('encomienda_items').delete().eq('id', target.id);
      // Agencia: se verifica la fila devuelta por .select(); un borrado que no
      // toca nada se registra en vez de darse por hecho.
      const { data, error } = isAgency ? await query.select() : await query;
      if (error) {
        console.error('[EncomiendaWizard] item delete failed:', error);
        alert('No se pudo eliminar el artículo: ' + error.message);
      } else if (isAgency && (!data || data.length === 0)) {
        console.warn('[EncomiendaWizard] item delete touched no rows:', { id: target.id, tempId });
      } else {
        console.log('[EncomiendaWizard] item deleted:', { id: target.id, tempId });
      }
    }
  };

  const updateItem = (pkgId, tempId, field, value) =>
    setPackages(p => ({
      ...p,
      [pkgId]: { ...p[pkgId], items: p[pkgId].items.map(it => it.tempId === tempId ? { ...it, [field]: value } : it) },
    }));

  const updateItemBatch = (pkgId, tempId, fields) =>
    setPackages(p => ({
      ...p,
      [pkgId]: { ...p[pkgId], items: p[pkgId].items.map(it => it.tempId === tempId ? { ...it, ...fields } : it) },
    }));

  // Reload a package's items from DB into local state (used after AI receipt save)
  const refreshPackageItems = async (pkgId) => {
    const { data: items } = await supabase
      .from('encomienda_items')
      .select(ITEM_SELECT)
      .eq('package_id', pkgId)
      .order('created_at', { ascending: true });
    if (!items) return;
    const formatted = items.map(mapItemAgency);
    setPackages(prev => ({
      ...prev,
      [pkgId]: { ...prev[pkgId], items: formatted },
    }));
  };

  const saveItemOnBlur = async (pkgId, item) => {
    // Run: ALTER TABLE encomienda_items ADD COLUMN IF NOT EXISTS catalog_id UUID REFERENCES customs_catalog(id);
    // Run: ALTER TABLE encomienda_items ADD COLUMN IF NOT EXISTS cip_usd DECIMAL(10,2);
    const desc = (item.description || '').trim();
    const val = parseFloat(item.unitValue);
    const hasValue = !Number.isNaN(val) && val > 0;
    const isBlank = desc === '' && !hasValue;

    // Guard 1: never persist an empty row on first blur.
    if (!item.id && isBlank) {
      return;
    }

    const payload = {
      package_id: pkgId,
      quantity: parseFloat(item.quantity) || 1,
      description: item.description || '',
      condition: item.condition || 'NUEVO',
      unit_value: parseFloat(item.unitValue) || 0,
      is_merchandise: true,
      catalog_id: item.catalog_id || null,
      cip_usd: item.cip_usd != null ? parseFloat(item.cip_usd) : null,
      // Agencia: el texto del cliente, la unidad y la bandera de revisión
      // también viajan (columnas de la migración description_raw / unidad_medida).
      ...(isAgency
        ? {
            description_raw: item.description_raw || '',
            unidad_medida: item.unidad_medida || 'UNIDAD',
            pending_review: Boolean(item.pending_review),
          }
        : {}),
    };

    if (item.id) {
      const upd = supabase.from('encomienda_items').update(payload).eq('id', item.id);
      inFlightItemSaves.current.add(upd);
      try {
        await upd;
      } finally {
        inFlightItemSaves.current.delete(upd);
      }
      return item.id;
    }

    // Guard 2: prevent duplicate INSERTs from rapid blurs before the first resolves.
    if (inFlightItemInserts.current.has(item.tempId)) {
      return null;
    }
    inFlightItemInserts.current.add(item.tempId);
    const ins = (async () => {
      try {
        const { data, error } = await supabase
          .from('encomienda_items')
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        if (data) updateItem(pkgId, item.tempId, 'id', data.id);
        console.log('[EncomiendaWizard] item inserted:', { tempId: item.tempId, id: data?.id, desc: payload.description });
        return data?.id ?? null;
      } catch (err) {
        console.error('[EncomiendaWizard] item insert failed:', err);
        return null;
      } finally {
        inFlightItemInserts.current.delete(item.tempId);
      }
    })();
    inFlightItemSaves.current.add(ins);
    try {
      return await ins;
    } finally {
      inFlightItemSaves.current.delete(ins);
    }
  };

  // ── Step 2: Add / Delete package ────────────────────────────────────────────
  const handleAddPackage = async () => {
    if (!encomiendaDbId) return;
    setAddingPackage(true);
    try {
      const nextNum = pkgIds.length + 1;
      let manifestId = currentManifest?.id || null;
      if (!manifestId) {
        const { data: openMf } = await supabase.from('manifests').select('id').eq('status', 'OPEN').maybeSingle();
        manifestId = openMf?.id || null;
      }
      const { data: newPkg, error } = await supabase
        .from('encomienda_packages')
        .insert([{
          encomienda_id: encomiendaDbId,
          recipient_name: recipientName.trim(),
          recipient_phone: composePhone(recipientCountry, recipientPhone) || null,
          recipient_address: [recipientAddress.trim(), recipientDepartamento.trim(), recipientMunicipio.trim()].filter(Boolean).join(', '),
          recipient_departamento: recipientDepartamento || null,
          recipient_municipio: recipientMunicipio || null,
          delivery_type: deliveryType,
          package_number: nextNum,
          entry_manifest_id: manifestId,
        }])
        .select()
        .single();
      if (error) throw error;
      await upsertNewDestinatarioIfNeeded();
      setPkgIds(prev => [...prev, newPkg.id]);
      setPackages(prev => ({ ...prev, [newPkg.id]: { items: [emptyItem()], photoUrl: null, recipient: recipientDesdePaqueteRow(newPkg), package_number: nextNum } }));
      return newPkg.id;
    } catch (err) {
      alert('Error adding package: ' + err.message);
    } finally {
      setAddingPackage(false);
    }
    return null;
  };

  const handleDeletePackage = async (pkgId) => {
    if (pkgIds.length <= 1) return;
    try {
      await supabase.from('encomienda_items').delete().eq('package_id', pkgId);
      await supabase.from('encomienda_packages').delete().eq('id', pkgId);
      const remaining = pkgIds.filter(id => id !== pkgId);
      // Renumber remaining packages in DB
      for (let i = 0; i < remaining.length; i++) {
        await supabase.from('encomienda_packages').update({ package_number: i + 1 }).eq('id', remaining[i]);
      }
      setPkgIds(remaining);
      setPackages(prev => {
        const next = { ...prev };
        delete next[pkgId];
        remaining.forEach((id, i) => {
          if (next[id]) next[id] = { ...next[id], package_number: i + 1 };
        });
        return next;
      });
    } catch (err) {
      alert('Error deleting package: ' + err.message);
    }
  };

  // ── Agencia: orquestador del paso 2 (no-op para los demás tenants) ──────────
  const agency = useAgencyStep2Controller({
    enabled: isAgency,
    tenantId: tenant?.id,
    currentManifestId: currentManifest?.id || null,
    encomiendaDbId,
    encomiendaPublicId,
    customerId,
    defaultRecipient,
    savedDestinatarios,
    packages,
    setPackages,
    pkgIds,
    setPkgIds,
    cajas,
    setCajas,
    emptyItem,
    saveItemOnBlur,
    removeItem,
    handleAddPackage,
    handleDeletePackage,
    deleteCaja,
    inFlightItemSaves,
    analyzingPackageId,
    setAnalyzingPackageId,
  });

  // ── Step 3: load fresh from DB ───────────────────────────────────────────────
  const loadReviewData = async () => {
    setReviewLoading(true);
    try {
      const { data } = await supabase
        .from('encomiendas')
        .select(`*, customers(full_name), encomienda_packages(*, encomienda_items(*))`)
        .eq('id', encomiendaDbId)
        .maybeSingle();
      setReviewData(data);

      const { data: boxData } = await supabase
        .from('boxes')
        .select('box_id, weight_lb, dimensions')
        .eq('encomienda_id', encomiendaDbId)
        .eq('tenant_id', tenant?.id)
        .order('created_at', { ascending: true });
      setReviewBoxes(boxData || []);
    } finally {
      setReviewLoading(false);
    }
  };

  const goToStep3 = async () => {
    if (isAgency) {
      // Agencia: una caja intacta (sin peso, dimensiones ni paquetes) no bloquea;
      // una incompleta avisa en línea, sin alert. Las ediciones pendientes se
      // guardan antes de leer la revisión desde la base.
      const incompletas = cajas.filter(c =>
        !c.saved && !(!c.weight_lb && !c.dimensions?.trim() && (c.packageIds || []).length === 0)
      );
      if (incompletas.length > 0) {
        agency.setNotice('Hay cajas incompletas: cada caja necesita peso, dimensiones y al menos un paquete antes de revisar totales.');
        return;
      }
      agency.setNotice(null);
      await agency.flush();
      setCurrentStep(3);
      loadReviewData();
      return;
    }
    // All cajas must be saved with ≥1 package before advancing
    const unsaved = cajas.filter(c => !c.saved);
    if (unsaved.length > 0) {
      alert('Guardá todas las cajas antes de continuar. Cada caja debe tener al menos un paquete.');
      return;
    }
    setCurrentStep(3);
    loadReviewData();
  };

  // ── Step 3: save for later ───────────────────────────────────────────────────
  const handleSaveForLater = async () => {
    // Cajas are saved atomically per-caja via saveCaja; nothing to flush here.
    localStorage.removeItem(DRAFT_KEY);
    setSaveForLaterMsg(wtr.savedDraftMsg);
    setTimeout(() => { setSaveForLaterMsg(''); resetWizard(); }, 2500);
  };

  // ── Step 3: confirm ──────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      // Update package FOB totals
      if (reviewData?.encomienda_packages) {
        for (const pkg of reviewData.encomienda_packages) {
          const items = pkg.encomienda_items || [];
          const fob = items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_value || 0), 0);
          await supabase.from('encomienda_packages').update({ total_fob: fob }).eq('id', pkg.id);
        }
      }

      // Edit mode: sequence any newly added packages, then return
      if (editMode) {
        // Find packages added since original confirm (null global_package_id only)
        const { data: unsequencedPkgs } = await supabase
          .from('encomienda_packages')
          .select('id, package_number')
          .eq('encomienda_id', encomiendaDbId)
          .is('global_package_id', null)
          .order('package_number', { ascending: true });

        if ((unsequencedPkgs || []).length > 0) {
          // Get open manifest
          const { data: openManifest } = await supabase.from('manifests').select('id').eq('status', 'OPEN').maybeSingle();

          if (openManifest) {
            // Count casillero shipments in this manifest
            const { count: casilleroCount } = await supabase
              .from('shipments')
              .select('*', { count: 'exact', head: true })
              .eq('entry_manifest_id', openManifest.id);

            // Count packages from OTHER complete encomiendas in this manifest
            const { data: otherCompleteEncs } = await supabase
              .from('encomiendas')
              .select('id')
              .eq('manifest_id', openManifest.id)
              .eq('status', 'COMPLETE')
              .neq('id', encomiendaDbId);

            let otherEncCount = 0;
            if ((otherCompleteEncs || []).length > 0) {
              const otherIds = otherCompleteEncs.map(e => e.id);
              const { count: epCount } = await supabase
                .from('encomienda_packages')
                .select('*', { count: 'exact', head: true })
                .in('encomienda_id', otherIds);
              otherEncCount = epCount || 0;
            }

            // Count already-sequenced packages of THIS encomienda
            // (they occupy slots — new packages must start after them)
            const { count: alreadySequenced } = await supabase
              .from('encomienda_packages')
              .select('*', { count: 'exact', head: true })
              .eq('encomienda_id', encomiendaDbId)
              .not('global_package_id', 'is', null);

            const totalSoFar = (casilleroCount || 0) + otherEncCount + (alreadySequenced || 0);

            const mParts = (openManifest.id || '').split('-');
            const mNum = mParts[2] || '000';

            for (let i = 0; i < unsequencedPkgs.length; i++) {
              const sequenceNum = totalSoFar + i + 1;
              const displayId = `${customerId}-${sequenceNum}`;
              const globalPkgId = 'M' + mNum + '-' + displayId;
              await supabase.from('encomienda_packages').update({
                global_sequence_number: sequenceNum,
                unique_package_id: displayId,
                global_package_id: globalPkgId,
                entry_manifest_id: openManifest.id,
              }).eq('id', unsequencedPkgs[i].id);
            }
          }
        }

        await supabase.from('encomiendas').update({ updated_at: new Date().toISOString() }).eq('id', encomiendaDbId);
        if (onComplete) onComplete();
        return;
      }

      // STEP A: Get current open manifest
      const { data: openManifest } = await supabase.from('manifests').select('id').eq('status', 'OPEN').maybeSingle();

      if (openManifest) {
        // STEP B: Count all confirmed packages in manifest, excluding this encomienda.
        // Casillero shipments + packages from OTHER already-complete encomiendas only.
        // DRAFT encomiendas do not consume sequence numbers.
        const { count: casilleroCount } = await supabase
          .from('shipments')
          .select('*', { count: 'exact', head: true })
          .eq('entry_manifest_id', openManifest.id);

        const { data: otherCompleteEncs } = await supabase
          .from('encomiendas')
          .select('id')
          .eq('manifest_id', openManifest.id)
          .eq('status', 'COMPLETE')
          .neq('id', encomiendaDbId);

        let priorEncCount = 0;
        if (otherCompleteEncs && otherCompleteEncs.length > 0) {
          const otherIds = otherCompleteEncs.map(e => e.id);
          const { count: epCount } = await supabase
            .from('encomienda_packages')
            .select('*', { count: 'exact', head: true })
            .in('encomienda_id', otherIds);
          priorEncCount = epCount || 0;
        }

        const totalSoFar = (casilleroCount || 0) + priorEncCount;

        // STEP C: Assign sequence numbers to this encomienda's packages
        // Run: ALTER TABLE encomienda_packages ADD COLUMN IF NOT EXISTS global_package_id TEXT;
        const { data: pkgRows } = await supabase
          .from('encomienda_packages')
          .select('id')
          .eq('encomienda_id', encomiendaDbId)
          .order('package_number', { ascending: true });

        const mParts = (openManifest.id || '').split('-');
        const mNum = mParts[2] || '000';

        for (let i = 0; i < (pkgRows || []).length; i++) {
          const sequenceNum = totalSoFar + i + 1;
          const displayId = `${customerId}-${sequenceNum}`;
          const globalPkgId = 'M' + mNum + '-' + displayId;
          await supabase.from('encomienda_packages').update({
            global_sequence_number: sequenceNum,
            unique_package_id: displayId,
            global_package_id: globalPkgId,
            entry_manifest_id: openManifest.id,
          }).eq('id', pkgRows[i].id);
        }
      }

      // STEP D: Mark encomienda as COMPLETE
      await supabase.from('encomiendas').update({
        status: 'COMPLETE',
        completed_at: new Date().toISOString(),
        completed_by: user?.username || '',
        updated_at: new Date().toISOString(),
      }).eq('id', encomiendaDbId);

      localStorage.removeItem(DRAFT_KEY);
      setSuccessMsg((wtr.successMsg || '').replace('{id}', encomiendaPublicId));
      setTimeout(() => resetWizard(), 3000);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setConfirming(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setEncomiendaDbId(null);
    setEncomiendaPublicId(null);
    setSenderPhone('');
    setSenderName('');
    setCustomerId(null);
    setIsNewCustomer(false);
    setCustomerBadge(null);
    setRecipientName('');
    setRecipientPhone('');
    setRecipientAddress('');
    setDeliveryType('Drop Off');
    setPackageDbId(null);
    setPkgIds([]);
    setCajas([{ localId: `caja-${Date.now()}`, dbBoxId: null, caja_number: 1, weight_lb: '', dimensions: '', packageIds: [], saved: false, saving: false }]);
    setPackages({});
    setReviewData(null);
    setReviewBoxes([]);
    setSuccessMsg('');
    setStep1Error('');
    setSaveForLaterMsg('');
  };

  // ── Step indicator ───────────────────────────────────────────────────────────
  const steps = [
    { num: 1, label: wtr.step1Label },
    { num: 2, label: wtr.step2Label },
    { num: 3, label: wtr.step3Label },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Language toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setLang(l => l === 'es' ? 'en' : 'es')}
          className="btn btn-secondary"
          style={{ fontSize: '10px' }}
        >
          <Languages size={13} /> {lang === 'es' ? 'EN' : 'ES'}
        </button>
      </div>

      {/* Draft banner */}
      {draftBanner && (
        <div className="tile" style={{ background: 'var(--amber-soft)', borderColor: 'var(--amber)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm font-medium" style={{ color: 'var(--amber)' }}>{wtr.hasDraft}</p>
            <div className="flex gap-2 shrink-0">
              <button onClick={continueDraft} className="btn btn-primary" style={{ background: 'var(--amber)' }}>
                {wtr.continueDraft}
              </button>
              <button onClick={discardDraft} className="btn btn-secondary">
                {wtr.newEntry}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-start mb-8">
        {steps.map((step, idx) => (
          <div key={step.num} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
                currentStep === step.num
                  ? 'bg-orange-500 border-orange-500 text-white shadow-md'
                  : currentStep > step.num
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'bg-white border-gray-300 text-gray-400'
              }`} style={{ 
                background: currentStep === step.num ? 'var(--avianca-red)' : currentStep > step.num ? 'var(--green)' : 'var(--tile)',
                borderColor: currentStep === step.num ? 'var(--avianca-red)' : currentStep > step.num ? 'var(--green)' : 'var(--border)',
                color: currentStep >= step.num ? '#fff' : 'var(--text-tertiary)'
              }}>
                {currentStep > step.num ? '✓' : step.num}
              </div>
              <span className="metric-label mt-2 text-center" style={{ color: currentStep === step.num ? 'var(--text-main)' : 'var(--text-tertiary)', fontSize: '9px' }}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className="flex-1 h-0.5 mx-2 mb-6 rounded" style={{ background: currentStep > step.num ? 'var(--green)' : 'var(--border)' }} />
            )}
          </div>
        ))}
      </div>

      {/* Success banner */}
      {successMsg && (
        <div className="badge badge-done w-full p-4 text-sm flex items-center gap-2">
          <CheckCircle size={18} /> {successMsg}
        </div>
      )}

      {/* ── STEP 1 ─────────────────────────────────────────────────────────── */}
      {currentStep === 1 && !successMsg && (
        <div className="tile space-y-6">
          {/* Sender */}
          <div>
            <h3 className="tile-title uppercase mb-4">{wtr.senderSection}</h3>
            <div className="space-y-3">
              <div className="relative">
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select
                    className="input"
                    style={{ width: '90px', flex: '0 0 auto' }}
                    value={senderCountry}
                    onChange={e => setSenderCountry(e.target.value)}
                  >
                    {PHONE_COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    className="input font-mono"
                    style={{ flex: 1 }}
                    placeholder={wtr.senderPhone}
                    value={senderPhone}
                    onChange={e => { setSenderPhone(e.target.value); setCustomerBadge(null); }}
                  />
                </div>
                {lookingUp && (
                  <span className="absolute right-3 top-2.5">
                    <Loader size={14} className="animate-spin text-gray-400" />
                  </span>
                )}
                {customerBadge === 'existing' && (
                  <span className="absolute right-3 top-2 badge badge-done">
                    {wtr.registeredClient}
                  </span>
                )}
                {customerBadge === 'new' && (
                  <span className="absolute right-3 top-2 badge badge-ship">
                    {wtr.newClient}
                  </span>
                )}
                {showSenderMatches && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                    marginTop: '4px', background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                    maxHeight: '240px', overflowY: 'auto',
                  }}>
                    {senderMatches.map(row => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => pickSenderMatch(row)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                          padding: '8px 12px', textAlign: 'left', background: 'transparent',
                          border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{row.id}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{row.full_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                className="input"
                placeholder={wtr.senderName}
                value={senderName}
                onChange={e => setSenderName(e.target.value)}
              />
            </div>
          </div>

          {/* Single Recipient */}
          <div>
            <h3 className="tile-title uppercase mb-4">{wtr.recipientSection}</h3>
            {savedDestinatarios.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label className="metric-label mb-1 block">Dirección guardada</label>
                <select
                  className="input"
                  value={selectedDestinatarioId}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedDestinatarioId(val);
                    if (val === '') {
                      setDestinatarioPickedFromBank(false);
                      return;
                    }
                    const addr = savedDestinatarios.find(a => a.id === val);
                    if (addr) {
                      applyDestinatario(addr);
                      setDestinatarioPickedFromBank(true);
                    }
                  }}
                >
                  <option value="">-- Nueva dirección --</option>
                  {savedDestinatarios.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.is_default ? '⭐ ' : ''}{a.label} — {a.recipient_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {savedDestinatarios.length === 0 && customerId && !isNewCustomer && (
              <p className="metric-sub mb-2" style={{ color: 'var(--text-tertiary)' }}>
                Este cliente no tiene direcciones guardadas.
              </p>
            )}
            <div className="tile" style={{ background: 'var(--bg)', border: 'none' }}>
              <div className="space-y-3">
                <input
                  className="input"
                  placeholder={wtr.recipientName}
                  value={recipientName}
                  onChange={e => setRecipientName(e.target.value)}
                />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select
                    className="input"
                    style={{ width: '90px', flex: '0 0 auto' }}
                    value={recipientCountry}
                    onChange={e => setRecipientCountry(e.target.value)}
                  >
                    {PHONE_COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    className="input"
                    style={{ flex: 1 }}
                    placeholder={wtr.recipientPhone}
                    value={recipientPhone}
                    onChange={e => setRecipientPhone(e.target.value)}
                  />
                </div>
                <input
                  className="input"
                  placeholder={wtr.recipientAddress}
                  value={recipientAddress}
                  onChange={e => setRecipientAddress(e.target.value)}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <select
                    className="input"
                    value={recipientDepartamento}
                    onChange={e => {
                      setRecipientDepartamento(e.target.value);
                      setRecipientMunicipio('');
                    }}
                  >
                    <option value="">-- Departamento --</option>
                    {SV_DEPARTAMENTOS.map(dep => (
                      <option key={dep.name} value={dep.name}>{dep.name}</option>
                    ))}
                  </select>
                  <select
                    className="input"
                    value={recipientMunicipio}
                    disabled={!recipientDepartamento}
                    onChange={e => setRecipientMunicipio(e.target.value)}
                  >
                    <option value="">-- Municipio --</option>
                    {(SV_DEPARTAMENTOS.find(d => d.name === recipientDepartamento)?.municipios || [])
                      .map(mun => (
                        <option key={mun.name} value={mun.name}>{mun.name}</option>
                      ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDeliveryType('Pick Up')}
                    className={`btn ${deliveryType === 'Pick Up' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ flex: 1, justifyContent: 'center', background: deliveryType === 'Pick Up' ? 'var(--green)' : '' }}
                  >
                    {wtr.pickUp}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryType('Drop Off')}
                    className={`btn ${deliveryType === 'Drop Off' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ flex: 1, justifyContent: 'center', background: deliveryType === 'Drop Off' ? 'var(--avianca-red)' : '' }}
                  >
                    {wtr.dropOff}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {step1Error && (
            <p className="badge badge-error w-full text-center p-3">
              <AlertCircle size={14} className="inline mr-2" /> {step1Error}
            </p>
          )}

          {step1Success && (
            <div className="badge badge-done w-full p-3 text-center">
              <CheckCircle size={16} className="inline mr-2" /> {wtr.clientMayLeave}
            </div>
          )}

          <button
            type="button"
            onClick={handleStep1Save}
            disabled={step1Saving || step1Success}
            className="btn btn-primary w-full"
            style={{ justifyContent: 'center', height: '48px', fontSize: '14px' }}
          >
            {step1Saving && <Loader size={16} className="animate-spin mr-2" />}
            {wtr.saveAndContinue}
          </button>
        </div>
      )}

      {/* ── STEP 2 ─────────────────────────────────────────────────────────── */}
      {currentStep === 2 && (
        <div className="space-y-4">
          {isAgency ? (
            <AgencyStep2 {...agency.props} />
          ) : (
          <>
          {/* Boxes section */}
          <div className="tile">
            <h3 className="tile-title uppercase mb-4">{wtr.boxesSection}</h3>
            <div className="space-y-3">
              {cajas.map((caja) => {
                // Packages already assigned to OTHER cajas in this session
                const takenElsewhere = new Set(
                  cajas.filter(c => c.localId !== caja.localId).flatMap(c => c.packageIds)
                );
                const availablePackages = pkgIds.filter(pid => !takenElsewhere.has(pid));

                return (
                  <div key={caja.localId} className="tile" style={{ background: 'var(--bg)', padding: '12px' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="row-id">{wtr.cajaLabel} {caja.caja_number}</span>
                      {caja.saved && <span className="badge badge-done">Guardada</span>}
                      <button
                        type="button"
                        onClick={() => deleteCaja(caja.localId)}
                        disabled={cajas.length === 1 && !caja.dbBoxId}
                        className="btn-ghost"
                        style={{ color: 'var(--avianca-red)', padding: '4px' }}
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input
                        type="number" min={0} step={0.1}
                        className="input"
                        placeholder={wtr.boxWeight}
                        value={caja.weight_lb}
                        onChange={e => setCajas(prev => prev.map(c =>
                          c.localId === caja.localId ? { ...c, weight_lb: e.target.value, saved: false } : c
                        ))}
                      />
                      <input
                        className="input"
                        placeholder={wtr.boxDimensions}
                        value={caja.dimensions}
                        onChange={e => setCajas(prev => prev.map(c =>
                          c.localId === caja.localId ? { ...c, dimensions: e.target.value, saved: false } : c
                        ))}
                      />
                    </div>

                    <div className="mb-2">
                      <p className="metric-label mb-1">Paquetes en esta caja</p>
                      {availablePackages.length === 0 ? (
                        <p className="metric-sub" style={{ color: 'var(--text-tertiary)' }}>
                          Todos los paquetes de esta encomienda ya están en otra caja.
                        </p>
                      ) : (
                        <div style={{ border: '1px solid var(--border)', borderRadius: '6px' }}>
                          {availablePackages.map(pkgId => {
                            const isChecked = caja.packageIds.includes(pkgId);
                            const pkgNumber = pkgIds.indexOf(pkgId) + 1;
                            return (
                              <label
                                key={pkgId}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', fontSize: '11px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={e => setCajas(prev => prev.map(c =>
                                    c.localId === caja.localId
                                      ? { ...c, packageIds: e.target.checked ? [...c.packageIds, pkgId] : c.packageIds.filter(id => id !== pkgId), saved: false }
                                      : c
                                  ))}
                                />
                                <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                  Paquete {pkgNumber}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => saveCaja(caja.localId)}
                      disabled={caja.saving || caja.packageIds.length === 0 || !caja.weight_lb || !caja.dimensions?.trim()}
                      className="btn btn-primary w-full"
                    >
                      {caja.saving ? 'Guardando...' : caja.dbBoxId ? 'Actualizar caja' : 'Guardar caja'}
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setCajas(prev => [
                ...prev,
                {
                  localId: `caja-${Date.now()}`,
                  dbBoxId: null,
                  caja_number: prev.length + 1,
                  weight_lb: '',
                  dimensions: '',
                  packageIds: [],
                  saved: false,
                  saving: false,
                },
              ])}
              className="btn btn-ghost mt-3"
              style={{ color: 'var(--avianca-red)', fontWeight: 'bold' }}
            >
              <Plus size={13} /> {wtr.addBox}
            </button>
          </div>

          {/* Package cards (items only) */}
          <div className="space-y-4">
            {pkgIds.map((pkgId, idx) => (
              <PackageCard
                key={pkgId}
                recipientName={recipientName}
                packageNumber={idx + 1}
                canDelete={pkgIds.length > 1}
                onDelete={() => handleDeletePackage(pkgId)}
                pkg={packages[pkgId]}
                pkgId={pkgId}
                encomiendaPublicId={encomiendaPublicId}
                wtr={wtr}
                onAddItem={() => addItem(pkgId)}
                onRemoveItem={tempId => removeItem(pkgId, tempId)}
                onUpdateItem={(tempId, field, value) => updateItem(pkgId, tempId, field, value)}
                onItemBlur={item => saveItemOnBlur(pkgId, item)}
                onBatchUpdateItem={(tempId, fields) => updateItemBatch(pkgId, tempId, fields)}
                onAnalyzeReceipt={() => setAnalyzingPackageId(pkgId)}
                isAnalyzing={analyzingPackageId === pkgId}
              />
            ))}
          </div>

          {/* Add package button */}
          <button
            type="button"
            onClick={handleAddPackage}
            disabled={addingPackage || !encomiendaDbId}
            className="btn btn-secondary w-full"
            style={{ borderStyle: 'dashed', borderWeight: '2px', height: '48px', justifyContent: 'center' }}
          >
            {addingPackage ? <Loader size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
            {wtr.addPackage}
          </button>

          {/* Running totals bar */}
          <div className="metric-strip">
            <div className="metric-card">
              <div className="metric-label">{wtr.totalItems}</div>
              <div className="metric-value" style={{ color: 'var(--blue-text)' }}>{totalItemCount}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">{wtr.totalFOB}</div>
              <div className="metric-value" style={{ color: 'var(--green)' }}>${totalFOB.toFixed(2)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">{wtr.estimatedTax}</div>
              <div className="metric-value" style={{ color: 'var(--amber)' }}>${estimatedTax.toFixed(2)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Peso Est.</div>
              <div className="metric-value">{totalBoxWeight.toFixed(1)}</div>
              <div className="metric-sub">{wtr.lbs}</div>
            </div>
          </div>
          </>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="btn btn-secondary px-8"
            >
              {wtr.backButton}
            </button>
            <button
              type="button"
              onClick={goToStep3}
              className="btn btn-primary flex-1 justify-center"
              style={{ height: '42px' }}
            >
              {wtr.reviewTotals}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3 ─────────────────────────────────────────────────────────── */}
      {currentStep === 3 && (
        <div className="space-y-4">
          {reviewLoading ? (
            <div className="empty-state">
              <Loader size={28} className="animate-spin mx-auto mb-3" />
              <p>{wtr.loadingReview}</p>
            </div>
          ) : reviewData ? (
            <>
              {/* Header card */}
              <div className="tile flex flex-wrap gap-4 justify-between items-center">
                <div>
                  <p className="metric-label">{wtr.encomiendaId}</p>
                  <p className="metric-value" style={{ color: 'var(--blue-text)' }}>{reviewData.unique_encomienda_id}</p>
                </div>
                <div className="text-right">
                  <p className="metric-label">{wtr.sender}</p>
                  <p className="tile-title">{reviewData.customers?.full_name || '—'}</p>
                </div>
                <span className="badge badge-pending">
                  {editMode ? 'MODO EDICIÓN' : 'ENCOMIENDA PENDIENTE'}
                </span>
              </div>

              {/* Boxes summary */}
              {reviewBoxes.length > 0 && (
                <div className="tile">
                  <p className="metric-label mb-3">{wtr.cajasRegistradas}</p>
                  <div className="space-y-2">
                    {reviewBoxes.map((box, i) => (
                      <div key={box.box_id} className="row-item">
                        <span className="row-id" style={{ width: 'auto', minWidth: '60px' }}>
                          {lang === 'es' ? 'Caja' : 'Box'} {i + 1}
                        </span>
                        <span className="text-sm font-mono">{box.weight_lb ? `${box.weight_lb} lbs` : '—'}</span>
                        <span className="metric-sub">{box.dimensions || '—'}</span>
                      </div>
                    ))}
                  </div>
                  <p className="metric-sub mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                    {wtr.cajasTotal}: {reviewBoxes.length} {wtr.cajas} • {reviewBoxes.reduce((s, b) => s + (parseFloat(b.weight_lb) || 0), 0).toFixed(1)} {wtr.lbs}
                  </p>
                </div>
              )}

              {/* Per-package tables */}
              <div className="space-y-4">
                {(reviewData.encomienda_packages || []).map(pkg => {
                  const items = pkg.encomienda_items || [];
                  const pkgFOB = items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_value || 0), 0);
                  const pkgTax = items
                    .filter(it => it.condition === 'NUEVO')
                    .reduce((s, it) => s + (it.quantity || 0) * (it.unit_value || 0) * 0.14, 0);
                  const pkgLabel = pkg.unique_package_id || `PK-${pkg.package_number}`;

                  return (
                    <div key={pkg.id} className="tile" style={{ padding: 0, overflow: 'hidden' }}>
                      <div className="p-4 bg-orange-50 border-b flex justify-between items-start" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                        <div>
                          <span className="badge badge-enc">{pkgLabel}</span>
                          <p className="tile-title mt-1">{pkg.recipient_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="badge badge-ship mb-1">{pkg.delivery_type}</p>
                          <p className="metric-sub max-w-[200px]">{pkg.recipient_address}</p>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 border-b" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                              <th className="text-center p-2 metric-label w-10">Cant</th>
                              <th className="text-left p-2 metric-label">Descripción</th>
                              <th className="text-center p-2 metric-label w-14">Cond.</th>
                              <th className="text-right p-2 metric-label w-20">V.Unit</th>
                              <th className="text-right p-2 metric-label w-20">FOB</th>
                              <th className="text-right p-2 metric-label w-20">Imp.</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50" style={{ borderColor: 'var(--border)' }}>
                            {items.map(it => {
                              const fob = (it.quantity || 0) * (it.unit_value || 0);
                              const tax = it.condition === 'NUEVO' ? fob * 0.14 : 0;
                              return (
                                <tr key={it.id} className="hover:bg-gray-50/50">
                                  <td className="p-2 text-center font-mono">{it.quantity}</td>
                                  <td className="p-2 text-gray-700" style={{ color: 'var(--text-main)' }}>{it.description}</td>
                                  <td className="p-2 text-center">
                                    <span className={`badge ${it.condition === 'NUEVO' ? 'badge-cas' : 'badge-hold'}`}>
                                      {it.condition}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-mono">${(it.unit_value || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right font-mono" style={{ color: 'var(--green)' }}>${fob.toFixed(2)}</td>
                                  <td className="p-2 text-right font-mono" style={{ color: 'var(--avianca-red)' }}>${tax.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot style={{ background: 'var(--bg)' }}>
                            <tr className="border-t" style={{ borderColor: 'var(--border)' }}>
                              <td colSpan={4} className="p-2 metric-label text-right pr-3">{pkgLabel}:</td>
                              <td className="p-2 text-right font-mono font-bold" style={{ color: 'var(--green)' }}>${pkgFOB.toFixed(2)}</td>
                              <td className="p-2 text-right font-mono font-bold" style={{ color: 'var(--avianca-red)' }}>${pkgTax.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grand totals bar */}
              {(() => {
                const allPkgs = reviewData.encomienda_packages || [];
                let gFOB = 0, gTax = 0, gItems = 0;
                for (const pkg of allPkgs) {
                  const items = pkg.encomienda_items || [];
                  gFOB += items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_value || 0), 0);
                  gTax += items.filter(it => it.condition === 'NUEVO')
                    .reduce((s, it) => s + (it.quantity || 0) * (it.unit_value || 0) * 0.14, 0);
                  gItems += sumQty(items);
                }
                return (
                  <div className="metric-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <div className="metric-card">
                      <div className="metric-label">{wtr.totalPackages}</div>
                      <div className="metric-value">{allPkgs.length}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">{wtr.totalItems}</div>
                      <div className="metric-value">{gItems}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">{wtr.totalFOBLabel}</div>
                      <div className="metric-value" style={{ color: 'var(--green)' }}>${gFOB.toFixed(2)}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">{wtr.totalTax}</div>
                      <div className="metric-value" style={{ color: 'var(--avianca-red)' }}>${gTax.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })()}

              {saveForLaterMsg && (
                <div className="badge badge-done w-full p-3 text-center">
                  <CheckCircle size={16} className="inline mr-2" /> {saveForLaterMsg}
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="btn btn-secondary px-8"
                >
                  {wtr.backToEdit}
                </button>
                {!editMode && (
                  <button
                    type="button"
                    onClick={handleSaveForLater}
                    disabled={confirming || !!saveForLaterMsg}
                    className="btn btn-secondary"
                    style={{ background: 'var(--amber-soft)', color: 'var(--amber)', borderColor: 'var(--amber)' }}
                  >
                    💾 {wtr.saveForLater}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={confirming || !!saveForLaterMsg}
                  className="btn btn-primary flex-1 justify-center"
                  style={{ height: '48px', fontSize: '16px' }}
                >
                  {confirming ? <Loader size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                  {editMode ? wtr.updateButton : wtr.confirmButton}
                </button>
              </div>
            </>
          ) : (
            <p className="empty-state">{wtr.noData}</p>
          )}
        </div>
      )}
      {/* ── AI Receipt Analyzer modal ── */}
      {analyzingPackageId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '16px', padding: '24px',
            width: '100%', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                Análisis de Recibo — Paquete {pkgIds.indexOf(analyzingPackageId) + 1}
              </span>
              <button
                type="button"
                onClick={() => setAnalyzingPackageId(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}
              >×</button>
            </div>
            <ReceiptAnalyzer
              packageId={analyzingPackageId}
              encomiendaId={encomiendaDbId}
              existingItems={packages[analyzingPackageId]?.items || []}
              onItemsConfirmed={async (items) => {
                const targetPkgId = analyzingPackageId;
                if (!targetPkgId) return;

                // Idempotency guard — fetch what's already in DB for this package
                // and skip inserting exact matches. Defends against any double-fire
                // reaching this callback (from anywhere).
                const { data: existing } = await supabase
                  .from('encomienda_items')
                  .select('description, quantity, unit_value, condition')
                  .eq('package_id', targetPkgId);

                const existingKeys = new Set(
                  (existing || []).map(e => `${(e.description || '').trim()}|${parseFloat(e.quantity) || 1}|${parseFloat(e.unit_value) || 0}|${e.condition || 'NUEVO'}`)
                );

                let insertedCount = 0;
                let skippedCount = 0;
                for (const item of items) {
                  const key = `${(item.description || '').trim()}|${parseFloat(item.quantity) || 1}|${parseFloat(item.unit_price) || 0}|NUEVO`;
                  if (existingKeys.has(key)) {
                    skippedCount++;
                    continue;
                  }
                  const { error } = await supabase.from('encomienda_items').insert([{
                    package_id: targetPkgId,
                    description: item.description,
                    quantity: parseFloat(item.quantity) || 1,
                    unit_value: parseFloat(item.unit_price) || 0,
                    condition: 'NUEVO',
                    is_merchandise: true,
                  }]);
                  if (error) {
                    console.error('[EncomiendaWizard] receipt item insert failed:', error);
                    continue;
                  }
                  existingKeys.add(key);
                  insertedCount++;
                }

                console.log('[EncomiendaWizard] receipt items processed:', { targetPkgId, insertedCount, skippedCount, totalRequested: items.length });

                await refreshPackageItems(targetPkgId);
                setAnalyzingPackageId(null);
              }}
              onClose={() => setAnalyzingPackageId(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EncomiendaWizard;
